import React, { useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Alert,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { createAdmin } from "../../services/apiServices";

const NewAdmin = ({ onSuccess, onCancel }) => {
  const [submitting, setSubmitting] = useState(false);
  const [apiResponse, setApiResponse] = useState({
    status: null, // 'success' | 'error'
    message: "",
  });

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: "",
      email: "",
      password: "",
      role: "admin", // ✅ default role
    },

    validationSchema: Yup.object().shape({
      name: Yup.string().required("Name is required"),
      email: Yup.string()
        .required("Email is required")
        .email("Invalid email format"),
      password: Yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters"),
      role: Yup.string().required("Role is required"),
    }),

    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        setApiResponse({ status: null, message: "" });

        await createAdmin({
          name: values.name,
          email: values.email,
          password: values.password,
          role: values.role, // ✅ send role
        });

        setApiResponse({
          status: "success",
          message: "Admin created successfully!",
        });

        validation.resetForm();

        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 1500);
        }
      } catch (error) {
        setApiResponse({
          status: "error",
          message:
            error.response?.data?.message ||
            "Failed to create admin. Please try again.",
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleCancel = () => {
    if (onCancel) onCancel();
    else if (onSuccess) onSuccess();
  };

  return (
    <div className="page-content d-flex justify-content-center min-vh-100">
      <Container fluid className="mw-50 w-50">
        {apiResponse.status && (
          <Alert
            color={apiResponse.status === "success" ? "success" : "danger"}
            className="mb-4">
            {apiResponse.message}
          </Alert>
        )}

        <Form
          onSubmit={validation.handleSubmit}
          className="p-4 border rounded bg-light">
          <h4 className="text-center mb-4">Create New Admin</h4>

          {/* Name */}
          <Row>
            <Col lg="12">
              <div className="mb-3">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter admin name"
                  value={validation.values.name}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    !!(validation.touched.name && validation.errors.name)
                  }
                  disabled={submitting}
                />
                {validation.touched.name && validation.errors.name && (
                  <div className="text-danger">{validation.errors.name}</div>
                )}
              </div>
            </Col>
          </Row>

          {/* Email */}
          <Row>
            <Col lg="12">
              <div className="mb-3">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter admin email"
                  value={validation.values.email}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    !!(validation.touched.email && validation.errors.email)
                  }
                  disabled={submitting}
                />
                {validation.touched.email && validation.errors.email && (
                  <div className="text-danger">{validation.errors.email}</div>
                )}
              </div>
            </Col>
          </Row>

          {/* Password */}
          <Row>
            <Col lg="12">
              <div className="mb-3">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Enter admin password"
                  value={validation.values.password}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    !!(
                      validation.touched.password && validation.errors.password
                    )
                  }
                  disabled={submitting}
                />
                {validation.touched.password && validation.errors.password && (
                  <div className="text-danger">
                    {validation.errors.password}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Role */}
          <Row>
            <Col lg="12">
              <div className="mb-3">
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  name="role"
                  type="select"
                  value={validation.values.role}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    !!(validation.touched.role && validation.errors.role)
                  }
                  disabled={submitting}>
                  <option value="">Select role</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Super Admin</option>
                </Input>
                {validation.touched.role && validation.errors.role && (
                  <div className="text-danger">{validation.errors.role}</div>
                )}
              </div>
            </Col>
          </Row>

          {/* Actions */}
          <div className="d-flex justify-content-between">
            <Button
              color="secondary"
              onClick={handleCancel}
              disabled={submitting}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                "Create Admin"
              )}
            </Button>
          </div>
        </Form>
      </Container>
    </div>
  );
};

export default NewAdmin;
